import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  CheckCircle2,
  Zap,
  ShieldCheck,
} from "lucide-react";

export const KyuStoreNeoStreetwear: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const sp = (delay: number, damping = 12, stiffness = 160) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Neo-Brutalist Streetwear Palette
  const bgRaw = "#0D0E12";
  const acidLime = "#D4FF00";
  const royalPurple = "#6366F1";
  const paperWhite = "#FFFFFF";

  // Marquee Ticker offset
  const tickerX = (frame * -4) % 800;

  // Phone Mockup Entry & Breathing Tilt
  const phoneSpring = sp(10, 14, 110);
  const phoneTilt = Math.sin(frame / 20) * 2 - 3;
  const phoneY = interpolate(phoneSpring, [0, 1], [100, 0]);

  // Floating Sticker 1 (Sticker Stamp "0.8s")
  const stampSpring = sp(25, 10, 180);
  const stampScale = interpolate(stampSpring, [0, 1], [0, 1]);

  // Floating Badge 2 ("Cek ID")
  const badgeSpring = sp(35, 12, 170);

  // Subtitles Safe Zone
  const SUBTITLES = [
    { from: 0, to: 120, text: "Masih jaman top-up game nunggu admin bales berjam-jam?", highlight: "nunggu admin" },
    { from: 120, to: 270, text: "Udah nunggu lama... salah ketik ID pula. Nih, web baru Kyu Store!", highlight: "web baru Kyu Store" },
    { from: 270, to: 480, text: "Tampilan baru jauh lebih kenceng, cukup ketik User ID langsung kedeteksi!", highlight: "langsung kedeteksi" },
    { from: 480, to: 690, text: "Bayar tinggal scan QRIS, 0.8 detik saldo langsung mendarat otomatis!", highlight: "0.8 detik" },
    { from: 690, to: 935, text: "Gak pake ribet login, gak pake password. Cobain di kyustore.com!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgRaw,
        color: paperWhite,
        fontFamily: "'Bebas Neue', 'Anton', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Heavy Halftone / Industrial Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(212, 255, 0, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 255, 0, 0.05) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* 2. Top Ticker Marquee Tape (Y: 250px) */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: -40,
          right: -40,
          height: 48,
          backgroundColor: acidLime,
          transform: "rotate(-2deg)",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          boxShadow: "0 8px 0px #000000",
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            whiteSpace: "nowrap",
            transform: `translateX(${tickerX}px)`,
            gap: 30,
            fontSize: 26,
            fontWeight: 900,
            color: "#000000",
            letterSpacing: "0.08em",
          }}
        >
          {Array(8)
            .fill(null)
            .map((_, i) => (
              <span key={i}>
                ★ KYUSTORE.COM ✦ TOP UP SERBA OTOMATIS ✦ 0.8S QRIS ✦ TANPA PASSWORD ✦
              </span>
            ))}
        </div>
      </div>

      {/* 3. Streetwear Headline (Y: 330px) */}
      <div
        style={{
          position: "absolute",
          top: 330,
          left: 60,
          right: 60,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          zIndex: 5,
        }}
      >
        <div
          style={{
            fontSize: 82,
            lineHeight: 0.9,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "-0.01em",
            color: paperWhite,
            textShadow: "4px 4px 0px #000000",
          }}
        >
          WEB REVOLUSI <br />
          <span style={{ color: acidLime, textShadow: "4px 4px 0px #6366F1" }}>
            KYU STORE 2.0
          </span>
        </div>
      </div>

      {/* 4. Central Hero: Real Mobile Device Mockup With Tangible Shadows (Y: 530 - 1380 px) */}
      <div
        style={{
          position: "absolute",
          top: 520,
          left: "50%",
          transform: `translateX(-50%) translateY(${phoneY}px) rotate(${phoneTilt}deg)`,
          width: 440,
          height: 840,
          backgroundColor: "#16181F",
          borderRadius: 44,
          border: "4px solid #FFFFFF",
          boxShadow: "16px 16px 0px #000000, 0 30px 60px rgba(0,0,0,0.8)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          zIndex: 2,
        }}
      >
        {/* Phone Notch & Speaker */}
        <div
          style={{
            height: 36,
            backgroundColor: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: 100, height: 14, backgroundColor: "#1A1A1A", borderRadius: 10 }} />
        </div>

        {/* Real Screenshot of kyustore.com */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <Img
            src={staticFile("kyustore_real_mobile.png")}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
            }}
          />
        </div>
      </div>

      {/* 5. Tangible Floating Streetwear Badges Breaking Device Bounds */}

      {/* Sticker 1: 0.8s Scan QRIS Stamp (Left Overlap) */}
      <div
        style={{
          position: "absolute",
          top: 660,
          left: 40,
          transform: `scale(${stampScale}) rotate(-8deg)`,
          backgroundColor: acidLime,
          color: "#000000",
          padding: "16px 24px",
          borderRadius: 16,
          border: "3px solid #000000",
          boxShadow: "8px 8px 0px #000000",
          zIndex: 8,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 900 }}>
          <Zap size={20} color="#000000" fill="#000000" />
          <span>INSTANT DISPATCH</span>
        </div>
        <div style={{ fontSize: 54, fontWeight: 900, lineHeight: 0.95 }}>
          0.8 DETIK
        </div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>SALDO MASUK OTOMATIS</div>
      </div>

      {/* Sticker 2: Cek ID Valid Badge (Right Overlap) */}
      <div
        style={{
          position: "absolute",
          top: 920,
          right: 35,
          transform: `scale(${interpolate(badgeSpring, [0, 1], [0, 1])}) rotate(6deg)`,
          backgroundColor: "#FFFFFF",
          color: "#000000",
          padding: "16px 24px",
          borderRadius: 18,
          border: "3px solid #000000",
          boxShadow: "8px 8px 0px #6366F1",
          zIndex: 8,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={24} color="#10B981" />
          <span style={{ fontSize: 24, fontWeight: 900 }}>AUTO CEK NICKNAME</span>
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 16,
            fontFamily: "monospace",
            backgroundColor: "#F1F5F9",
            padding: "4px 10px",
            borderRadius: 8,
            fontWeight: 700,
          }}
        >
          ID: 88492019 ➔ ZURA (VALID)
        </div>
      </div>

      {/* Sticker 3: Tanpa Password (Bottom Right Overlap) */}
      <div
        style={{
          position: "absolute",
          top: 1150,
          left: 50,
          transform: "rotate(-4deg)",
          backgroundColor: royalPurple,
          color: "#FFFFFF",
          padding: "12px 22px",
          borderRadius: 14,
          border: "3px solid #000000",
          boxShadow: "6px 6px 0px #000000",
          zIndex: 8,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <ShieldCheck size={24} color={acidLime} />
        <span style={{ fontSize: 22, fontWeight: 900, letterSpacing: "0.04em" }}>
          100% AMAN TANPA PASSWORD
        </span>
      </div>

      {/* 6. Subtitle Safe Zone (Bottom 390px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: 60,
            right: 60,
            display: "flex",
            justifyContent: "center",
            zIndex: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#000000",
              border: `3px solid ${acidLime}`,
              borderRadius: 16,
              padding: "16px 28px",
              boxShadow: "8px 8px 0px #6366F1",
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: "#FFFFFF",
                lineHeight: 1.2,
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              {currentSub.text.split(currentSub.highlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      style={{
                        color: acidLime,
                        backgroundColor: "rgba(212, 255, 0, 0.15)",
                        padding: "2px 6px",
                        borderRadius: 6,
                      }}
                    >
                      {currentSub.highlight}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* 7. Master Audio Sync */}
      <Audio
        src={staticFile("kyustore_web_promo_enceladus.mp3")}
        volume={1.0}
      />
    </AbsoluteFill>
  );
};
